import React, { useContext, useMemo, useState } from 'react';
import { Link, Typography, Box, Badge } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid-pro';

import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { IssuedManualsContext } from '../context';
import { ActionMenu } from 'components/_atoms';
import datetimeHelper from 'helpers/datetimeHelper';
import { ManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import ExportModal from '../_components/ExportModal';
import IssuedManualsPolicyPageToolbar from '../_components/PolicyPageToolbar';
import useRelatedOrganizations from 'views/App/Portal/Dashboard/RiskPoolDashboard/_data_sources/useRelatedOrganizations';
import useJurisdictions from 'data_sources/useJurisdictions';
import errorHelper from 'helpers/errorHelper';

const IssuedManualsList: React.FC = () => {
  const { currentProfile, currentOrganizationId, isLexipolStaff } = useContext(AppContext);
  const isRiskPoolUser = currentProfile?.organization?.riskPoolEnabled;
  const { manuals, manualHasPermissions, manualsQuery, params, updateParams } = useContext(IssuedManualsContext);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedManualForExport, setSelectedManualForExport] = useState<ManualItem | null>(null);

  const handleExportClick = (manual: ManualItem) => {
    setSelectedManualForExport(manual);
    setExportModalOpen(true);
  };

  const { data: relatedOrgsData } = useRelatedOrganizations(
    currentOrganizationId || '',
    { enabled: !!isRiskPoolUser && !!currentOrganizationId },
    (errors) => errorHelper.enqueueErrors(errors)
  );

  const { data: jurisdictions } = useJurisdictions(
    { enabled: !!isLexipolStaff },
    (errors) => errorHelper.enqueueErrors(errors)
  );

  const organizationOptions = useMemo(() => {
    if (!relatedOrgsData?.content) return [];
    return relatedOrgsData.content
      .map((org) => ({ value: org.id, label: org.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [relatedOrgsData]);

  // Create filter options for jurisdictions
  const jurisdictionOptions = useMemo(() => {
    if (!jurisdictions) return [];
    return jurisdictions.map((jurisdiction) => ({
      value: jurisdiction.id,
      label: jurisdiction.name,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [jurisdictions]);

  const isFiltered = useMemo(() => {
    return params.search || params.orgId || params.jurisdictionId;
  }, [params]);

  const { clearParams } = useContext(IssuedManualsContext);

  const manualActions: (manual: ManualItem) => ActionMenuItemProps[] = (manual) => {
    const actions: ActionMenuItemProps[] = [];
    if (manualHasPermissions(manual.id, ['getIssuedManual'])) {
      actions.push({
        key: `pdf_${manual.id}`,
        label: t('policy.issuedManuals.actions.pdf'),
        onClick: () => handleExportClick(manual),
      });
    }
    if (manualHasPermissions(manual.id, ['listManualAcknowledgementSchedule'])) {
      actions.push({
        key: `schedules_${manual.id}`,
        label: t('policy.issuedManuals.actions.acknowledgementSchedules'),
        href: `./${manual.id}/acknowledgement-schedules`,
      });
    }
    if (manualHasPermissions(manual.id, ['issuePolicy'])) {
      if (manual.organizationId === 'lexipoladminstaffuuid') {
        actions.push({
          key: `${manual.id}_distribute_changes`,
          label: 'Distribute Policies',
          href: `./${manual.id}/distribute-changes`,
        });
      }
    }
    return actions;
  };

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('policy.manuals.properties.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;

          const isRiskPoolUser = currentProfile?.organization?.riskPoolEnabled === true;

          if (isRiskPoolUser) {
            return (
              <Typography
                variant='h6'
              >
                {row.name?.value || row.name}
              </Typography>
            );
          }

          return (
            <Link href={`./${row.id}`}>
              <Typography
                variant='h6'
                sx={{ fontWeight: 'semibold' }}
              >
                {row.name?.value || row.name}
              </Typography>
            </Link>
          );
        },
      },
      ...(currentProfile?.organization?.riskPoolEnabled && {
        organization: {
          header: t('policy.manuals.properties.organization'),
          flex: 1.5,
          display: 'flex',
          renderer: (params: GridCellParams) => {
            const row = params.row;
            if (!row) return null;
            return <Typography>{row.organization?.name}</Typography>;
          },
        },
      }),
      'vertical.name': {
        header: t('policy.manuals.properties.vertical'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{row.vertical?.value || row.vertical?.name}</Typography>;
        },
      },
      'manualType.name': {
        header: t('policy.manuals.properties.manualType'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{row.manualType?.value || row.manualType?.name}</Typography>;
        },
      },
      publishedAt: {
        header: t('policy.manuals.properties.publishedAt'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <Typography>{datetimeHelper.formatDate(row.publishedAt)}</Typography>;
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.25,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <ActionMenu
              id={`action_menu_${row.id}`}
              items={manualActions(row)}
            />
          );
        },
      },
    }),
    [t, manualActions]
  );

  const gridViewQuery = useMemo(() => {
    // Use content length as fallback for count if it's undefined
    const pageCount = manualsQuery.data?.pages?.[0]?.count ?? manualsQuery.data?.pages?.[0]?.content?.length ?? 0;

    return {
      hasNextPage: manualsQuery.hasNextPage,
      isFetchingNextPage: manualsQuery.isFetchingNextPage,
      fetchNextPage: manualsQuery.fetchNextPage,
      isError: manualsQuery.isError,
      isLoading: manualsQuery.isLoading,
      error: manualsQuery.error,
      data: {
        pages:
          manualsQuery.data?.pages?.map((page) => ({
            count: page.count ?? page.content?.length ?? 0,
            rows: page.content,
          })) || [],
      },
    };
  }, [manualsQuery]);

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.issuedManuals.title'),
        subtitle: isRiskPoolUser
          ? t('policy.issuedManuals.subtitleRiskPool')
          : t('policy.issuedManuals.subtitle'),
        titleBadge:
          (manualsQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={manualsQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <IssuedManualsPolicyPageToolbar
          params={params}
          updateParams={updateParams}
          isFiltered={!!isFiltered}
          clearParams={clearParams}
          jurisdictionOptions={jurisdictionOptions}
          organizationOptions={organizationOptions}
        />
        <GridSection
          columns={columns}
          data={manuals}
          query={gridViewQuery}
        />
      </Box>
      {selectedManualForExport && (
        <ExportModal
          open={exportModalOpen}
          onClose={() => {
            setExportModalOpen(false);
            setSelectedManualForExport(null);
          }}
          exportType='manual'
          manual={selectedManualForExport}
        />
      )}
    </CommonLayout>
  );
};

export default IssuedManualsList;
